import { useState } from "react";
import { Link } from "react-router-dom";
import { Plug, FileUp, Calculator, Vault, FileCheck, CreditCard, FolderCheck, ShieldCheck,
  ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { API, errMsg } from "../../lib/api";
import { moneyExact } from "../../lib/format";

const STEPS = [
  { icon: Plug, title: "Connect Your Business", text: "Link your merchant processors, banks, and accounting tools — or enter sales manually." },
  { icon: FileUp, title: "Import Sales", text: "Transactions flow in from Stripe, Square, Clover, Shopify, and more, or via CSV." },
  { icon: Calculator, title: "Calculate Tax Obligations", text: "Database-driven Arizona TPT rules calculate exactly what you owe, and keep the math on record." },
  { icon: Vault, title: "Reserve Tax Funds", text: "The Tax Vault shows what to set aside so tax money never mixes with operating cash." },
  { icon: FileCheck, title: "Prepare and File", text: "Returns are prepared, reviewed by a tax professional, and filed with your authorization." },
  { icon: CreditCard, title: "Make Payment", text: "Schedule and track payments to the tax authorities with confirmation numbers stored." },
  { icon: FolderCheck, title: "Store Confirmation and Records", text: "Every filing, payment, and document is kept audit-ready in your vault. Forever." },
];

const FEATURES = [
  ["Tax Dashboard", "Your entire tax position at a glance: sales, liability, reserves, deadlines."],
  ["Tax Vault", "A reserve ledger that separates tax money from spendable revenue."],
  ["TPT Management", "Arizona Transaction Privilege Tax periods, filings, and payments handled end-to-end."],
  ["Past-Due Tax Rescue", "We reconstruct missing months, estimate exposure, and get you current."],
  ["Monthly Tax Management", "Every period tracked from first sale to filed and paid."],
  ["Tax Calendar", "Every filing, payment, and document deadline, color-coded."],
  ["Merchant Reconciliation", "Merchant totals vs. bank deposits vs. reported sales — discrepancies flagged."],
  ["Audit-Ready Records", "Generate a complete tax package for any period in one click."],
  ["Accountant & CPA Collaboration", "Your tax professional gets their own dashboard and review workflow."],
  ["Business Tax Autopilot", "Full-service: we monitor, reconcile, file, and coordinate payment."],
];

function Estimator() {
  const [sales, setSales] = useState("");
  const [rate, setRate] = useState("8.6");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await API.post("/public/calculator", { monthly_sales: sales, rate_percent: rate });
      setResult(data);
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  return (
    <div className="card-gov border-t-4 border-t-gold p-6" data-testid="tax-estimator">
      <h3 className="text-lg font-bold text-navy">TPT Tax Estimator</h3>
      <form onSubmit={run} className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label-gov">Monthly Sales</label>
          <input className="input-gov money" inputMode="decimal" placeholder="96070" value={sales}
            onChange={(e) => setSales(e.target.value)} data-testid="estimator-sales" required />
        </div>
        <div>
          <label className="label-gov">Estimated Tax Rate %</label>
          <input className="input-gov money" inputMode="decimal" value={rate}
            onChange={(e) => setRate(e.target.value)} data-testid="estimator-rate" required />
        </div>
        <button className="btn-navy sm:col-span-2" disabled={busy} data-testid="estimator-calculate">
          {busy ? "Calculating…" : "Estimate My Tax Obligation"}
        </button>
      </form>
      {err && <div className="mt-3 text-sm text-rose-700" data-testid="estimator-error">{err}</div>}
      {result && (
        <div className="mt-4 rounded-md border border-gold/50 bg-gold-50 p-4" data-testid="estimator-result">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Estimated Monthly Tax Obligation</div>
          <div className="money mt-1 text-3xl font-bold text-navy">{moneyExact(result.estimated_tax_cents)}</div>
          <p className="mt-2 text-xs text-slate-500">{result.disclaimer}</p>
          <Link to="/register" className="btn-copper mt-3 w-full" data-testid="estimator-cta">Let AZ Tax Accounts Manage It For You</Link>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative border-b border-slate-200 bg-white">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-copper/30 bg-copper-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-copper">
              <ShieldCheck className="h-3.5 w-3.5" /> Arizona Business Tax Compliance Platform
            </div>
            <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl lg:text-6xl">
              Business Tax Management <span className="text-copper">Made Simple</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-slate-600">
              Connect your sales. Calculate your taxes. Reserve what you owe.
              File on time. Pay securely. Keep proof forever.
            </p>
            <p className="mt-3 text-sm font-semibold uppercase tracking-widest text-slate-500">
              Know what belongs to your business — and what belongs to the tax authorities.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="btn-copper px-6 py-3 text-base" data-testid="hero-get-started">Get Started <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/consultation" className="btn-outline-gov px-6 py-3 text-base" data-testid="hero-book-consultation">Book a Consultation</Link>
              <Link to="/login" className="px-6 py-3 text-base font-semibold text-slate-500 hover:text-navy" data-testid="hero-login">Login</Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-lg">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">September Tax Status</span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">You're on track</span>
              </div>
              <dl className="mt-4 space-y-3 text-sm">
                {[["Sales", "$96,070"], ["Tax Due", "$6,487"], ["Tax Reserved", "$6,487"]].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <dt className="text-slate-500">{k}</dt><dd className="money text-lg font-bold text-navy">{v}</dd>
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Tax Vault</dt>
                  <dd className="flex items-center gap-2"><div className="h-2 w-28 overflow-hidden rounded-full bg-slate-200"><div className="h-full w-full bg-emerald-500" /></div><span className="text-xs font-bold text-emerald-600">100% FUNDED</span></dd>
                </div>
                {[["Return", "READY TO FILE", "text-sky-700"], ["Payment", "SCHEDULED", "text-sky-700"], ["Due Date", "SEPTEMBER 20", "text-amber-600"]].map(([k, v, c]) => (
                  <div key={k} className="flex items-center justify-between">
                    <dt className="text-slate-500">{k}</dt><dd className={`text-sm font-bold ${c}`}>{v}</dd>
                  </div>
                ))}
                <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-3">
                  <dt className="font-semibold text-slate-600">Safe To Spend</dt>
                  <dd className="money text-2xl font-bold text-emerald-600">$47,820</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Callout */}
      <section className="border-y-4 border-copper bg-copper-50">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-4 py-10 text-center sm:px-6">
          <AlertTriangle className="h-8 w-8 text-copper" />
          <h2 className="text-2xl font-extrabold tracking-tight text-copper sm:text-3xl">STOP SPENDING TAX MONEY BY ACCIDENT.</h2>
          <p className="max-w-2xl text-slate-600">AZ Tax Accounts helps businesses clearly separate expected tax obligations from operating funds — so the money for the Arizona Department of Revenue is never accidentally spent on payroll or inventory.</p>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6" id="how-it-works">
        <div className="text-center">
          <div className="text-xs font-semibold uppercase tracking-widest text-copper">The Process</div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-navy sm:text-4xl">How It Works</h2>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="card-gov group p-5 transition-transform hover:-translate-y-0.5" data-testid={`step-${i + 1}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white group-hover:bg-copper"><s.icon className="h-5 w-5" /></div>
                <div className="text-xs font-bold uppercase tracking-widest text-gold">Step {i + 1}</div>
              </div>
              <h3 className="mt-3 font-bold text-navy">{s.title}</h3>
              <p className="mt-1 text-sm text-slate-500">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features + estimator */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-copper">One Platform</div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-navy">Everything Between a Sale and a Filed Return</h2>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {FEATURES.map(([t, d]) => (
                <div key={t} className="flex gap-3 rounded-md border border-slate-200 p-4" data-testid={`feature-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <div><div className="font-semibold text-navy">{t}</div><div className="mt-0.5 text-sm text-slate-500">{d}</div></div>
                </div>
              ))}
            </div>
          </div>
          <Estimator />
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-14 text-center sm:px-6">
          <h2 className="text-3xl font-bold tracking-tight text-navy">Ready to separate tax money from your money?</h2>
          <p className="mx-auto mt-3 max-w-xl text-slate-600">Start with a dashboard that answers: how much did I make, how much is taxable, how much do I owe, and how much is safe to spend.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/register" className="btn-copper px-6 py-3" data-testid="cta-get-started">Get Started</Link>
            <Link to="/pricing" className="btn-outline-gov px-6 py-3" data-testid="cta-pricing">See Pricing</Link>
            <Link to="/tax-rescue" className="px-6 py-3 font-semibold text-copper hover:text-copper-light" data-testid="cta-rescue">Behind on taxes? AZ Tax Rescue →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
